<template>
  <!-- Overlay for mobile -->
  <div
    v-if="mobileDevice && sidebarOpen"
    class="fixed inset-0 bg-black bg-opacity-50 z-[10030] md:hidden"
    @click="sidebarOpen = false"
  ></div>

  <aside
    class="bg-[#191919] h-full duration-150 z-[10030] overflow-auto scroll-hidden transition-all"
    :class="[
      mobileDevice && sidebarOpen ? 'fixed left-0 top-0 w-[80vw] max-w-xs h-screen md:hidden' : '',
      mobileDevice && !sidebarOpen ? 'hidden' : '',
      !mobileDevice
        ? sidebarOpen
          ? 'max-w-[300px] w-[300px]'
          : 'w-[85px] min-w-[85px] !max-w-[85px]'
        : '',
      '',
    ]"
    ref="sidebarRef"
  >
    <!-- Top bar: Logo + Hamburger (close) icon on mobile -->
    <div
      class="flex items-center justify-between px-4 py-[7.1px] sticky top-0 bg-[#191919] z-10 overflow-y-auto scroll-hidden"
    >
      <router-link to="/" class="flex items-center gap-2">
        <img
          class="logo py-[6px] bg-[#191919] w-auto"
          :class="'h-12'"
          :src="sidebarOpen ? Logo : MobileLogo"
          alt="logo"
        />
      </router-link>

      <!-- Hamburger/close icon on mobile -->
      <button
        v-if="mobileDevice && sidebarOpen"
        class="md:hidden text-white text-2xl bg-[#292929] rounded-full p-2 hover:bg-[#444] transition ml-2"
        @click.stop="sidebarOpen = false"
        aria-label="Close sidebar"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="w-6 h-6"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div v-if="loadingNavigation" class="flex h-[90vh] w-full justify-center items-center py-8">
      <Loader :loading="loadingNavigation" />
    </div>

    <router-link
      v-if="can('access-new-dashboard')"
      to="/dashboard"
      class="flex items-center gap-2"
      :active-class="'nav-item-active'"
    >
      <li
        class="parent-menu flex flex-col w-full relative"
        @mouseenter="handleMouseEnter(-1, $event)"
        @mouseleave="handleMouseLeave"
        :class="{
          'sidebar-listitem--hovered': !sidebarOpen && popoverIndex === -1,
        }"
      >
        <button
          class="flex items-center w-full p-4 text-white transition-all duration-200"
          :class="
            sidebarOpen
              ? 'justify-start hover:bg-[#292929] text-[1rem]'
              : 'justify-center hover:bg-[#404040] flex-col gap-2 text-[10px]'
          "
        >
          <component
            :is="lucideIcons['LayoutDashboard']"
            class="w-6 h-6 text-red-500"
            :class="sidebarOpen ? 'size-6 text-white inline-block mr-6' : 'size-6 text-white'"
          />
          <span>Dashboard</span>
        </button>
      </li>
    </router-link>

    <template v-if="navigation?.length && !loadingNavigation">
      <ul class="navigation-list relative">
        <!--
          The scrolling half. Only the links scroll; Logout below sits outside
          this box so it is always on screen — see .navigation-items.
        -->
        <div class="navigation-items">
          <!-- Dashboard -->

          <!-- Navigation Items -->
          <li
            v-for="(item, index) in navigation"
            :key="index"
            class="parent-menu flex flex-col w-full relative"
            @mouseenter="handleMouseEnter(index, $event)"
            @mouseleave="handleMouseLeave"
            :class="{
              'sidebar-listitem--hovered': !sidebarOpen && popoverIndex === index,
            }"
          >
            <button
              v-if="item.children?.length"
              class="flex items-center w-full p-4 text-white transition-all duration-200"
              :class="
                sidebarOpen
                  ? 'justify-start hover:bg-[#292929] text-[1rem]'
                  : 'justify-center text-center hover:bg-[#404040] flex-col gap-2 text-[10px] text-center'
              "
              @click="sidebarOpen && toggleMenu(index)"
            >
              <!-- Dynamic icon or fallback to ListBulletIcon -->

              <component
                :is="
                  lucideIcons[
                    typeof item.icon_id === 'string' && item.icon_id && item.icon_id in lucideIcons
                      ? item.icon_id
                      : 'List'
                  ]
                "
                class="w-6 h-6 text-red-500"
                :class="sidebarOpen ? 'size-6 text-white inline-block mr-6' : 'size-6 text-white'"
              />

              <span>
                {{ item.name == 'Dashboard' ? 'Dashboard (legacy)' : item.name }}
              </span>
              <ChevronRightIcon
                class="size-4 text-white inline-block menu-icon transition-transform duration-100 ml-auto"
                :class="{ 'rotate-90': openIndex === index }"
                v-if="item.children?.length && sidebarOpen"
              />
            </button>

            <a
              v-else
              class="flex items-center w-full p-4 text-white hover:bg-[#292929] transition-all duration-200"
              :class="
                sidebarOpen
                  ? 'justify-start hover:bg-[#292929] text-[1rem]'
                  : 'justify-center hover:bg-[#404040] flex-col gap-2 text-[10px] text-center'
              "
              :href="menuHref(item.to)"
            >
              <!-- Dynamic icon or fallback to ListBulletIcon -->
              <component
                :is="
                  lucideIcons[
                    typeof item.icon_id === 'string' && item.icon_id && item.icon_id in lucideIcons
                      ? item.icon_id
                      : 'List'
                  ]
                "
                class="w-6 h-6 text-red-500"
                :class="sidebarOpen ? 'size-6 text-white inline-block mr-6' : 'size-6 text-white'"
              />

              <span>
                {{ item.name == 'Dashboard' ? 'Dashboard (legacy)' : item.name }}
              </span>
            </a>

            <!-- Popover for menu item -->
            <div
              v-if="!sidebarOpen && item.children?.length && popoverIndex === index"
              :class="['sidebar-popover', 'sidebar-popover--hovered']"
              :style="{
                top: popoverTop + 'px',
                left: '85px',
                position: 'fixed',
              }"
            >
              <!-- Children in popover -->
              <div v-if="item.children?.length" class="border-t border-[#3a3a3a]">
                <div class="space-y-1">
                  <a
                    v-for="(child, cIndex) in item.children"
                    :key="cIndex"
                    class="flex items-center gap-3 p-3 text-white text-sm hover:bg-[#292929] transition-all duration-200 rounded"
                    :href="menuHref(child.to)"
                  >
                    <span>{{ child.name }}</span>
                  </a>
                </div>
              </div>
            </div>

            <!-- Desktop submenu -->
            <transition name="collapse" @before-enter="beforeEnter" @enter="enter" @leave="leave">
              <ul
                v-if="openIndex === index && sidebarOpen"
                class="children flex flex-col bg-[#292929]"
              >
                <li
                  v-for="(child, cIndex) in item.children"
                  :key="cIndex"
                  class="child-menu flex w-full border-b-[1px] border-[#3a3a3a] last-of-type:border-b-0"
                >
                  <a
                    class="flex items-center p-3 text-white text-[1rem] hover:bg-[#292929] w-full transition-all duration-200 pl-10"
                    :href="menuHref(child.to)"
                  >
                    {{ child.name }}
                  </a>
                </li>
              </ul>
            </transition>
          </li>

          <!-- Companies Menu -->
          <li
            v-if="can('view-companies-module')"
            class="parent-menu flex flex-col w-full relative"
            @mouseenter="handleMouseEnter(-4, $event)"
            @mouseleave="handleMouseLeave"
            :class="{
              'sidebar-listitem--hovered': !sidebarOpen && popoverIndex === -4,
            }"
          >
            <button
              class="flex items-center w-full p-4 text-white transition-all duration-200"
              :class="
                sidebarOpen
                  ? 'justify-start hover:bg-[#292929] text-[1rem]'
                  : 'justify-center text-center hover:bg-[#404040] flex-col gap-2 text-[10px] text-center'
              "
              @click="sidebarOpen && toggleMenu(-4)"
            >
              <component
                :is="lucideIcons['Building2']"
                class="w-6 h-6 text-red-500"
                :class="sidebarOpen ? 'size-6 text-white inline-block mr-6' : 'size-6 text-white'"
              />
              <span>Companies</span>
              <ChevronRightIcon
                class="size-4 text-white inline-block menu-icon transition-transform duration-100 ml-auto"
                :class="{ 'rotate-90': openIndex === -4 }"
                v-if="sidebarOpen"
              />
            </button>

            <!-- Popover for Companies menu -->
            <div
              v-if="!sidebarOpen && popoverIndex === -4"
              :class="['sidebar-popover', 'sidebar-popover--hovered']"
              :style="{
                top: popoverTop + 'px',
                left: '85px',
                position: 'fixed',
              }"
            >
              <div class="border-t border-[#3a3a3a]">
                <div class="space-y-1">
                  <a
                    class="flex items-center gap-3 p-3 text-white text-sm hover:bg-[#292929] transition-all duration-200 rounded"
                    :href="menuHref('/companies')"
                  >
                    <span>All Companies</span>
                  </a>
                  <a
                    class="flex items-center gap-3 p-3 text-white text-sm hover:bg-[#292929] transition-all duration-200 rounded"
                    :href="'/buying-groups-sales-reps'"
                  >
                    <span>Kickback & Commision</span>
                  </a>
                </div>
              </div>
            </div>

            <!-- Desktop submenu for Companies -->
            <transition name="collapse" @before-enter="beforeEnter" @enter="enter" @leave="leave">
              <ul
                v-if="openIndex === -4 && sidebarOpen"
                class="children flex flex-col bg-[#292929]"
              >
                <li class="child-menu flex w-full border-b-[1px] border-[#3a3a3a]">
                  <a
                    class="flex items-center p-3 text-white text-[1rem] hover:bg-[#292929] w-full transition-all duration-200 pl-10"
                    :href="menuHref('/companies')"
                  >
                    All Companies
                  </a>
                </li>
                <li class="child-menu flex w-full last-of-type:border-b-0">
                  <a
                    class="flex items-center p-3 text-white text-[1rem] hover:bg-[#292929] w-full transition-all duration-200 pl-10"
                    :href="'/buying-groups-sales-reps'"
                  >
                    Kickback & Commision
                  </a>
                </li>
              </ul>
            </transition>
          </li>

          <!-- Tracking Master Dashboard (US #18826, task 18973). v2-native
               route, so it uses router-link (not the appURL-prefixed nav list).
               Role-gated to Logistics/CSM via view-tracking-dashboard; the
               access-new-dashboard fallback keeps it visible until that
               permission is provisioned. -->
          <router-link
            v-if="canSeeTracking"
            to="/tracking"
            class="flex items-center gap-2"
            :active-class="'nav-item-active'"
          >
            <li
              class="parent-menu flex flex-col w-full relative"
              @mouseenter="handleMouseEnter(-11, $event)"
              @mouseleave="handleMouseLeave"
              :class="{
                'sidebar-listitem--hovered': !sidebarOpen && popoverIndex === -11,
              }"
            >
              <button
                class="flex items-center w-full p-4 text-white transition-all duration-200"
                :class="
                  sidebarOpen
                    ? 'justify-start hover:bg-[#292929] text-[1rem]'
                    : 'justify-center hover:bg-[#404040] flex-col gap-2 text-[10px]'
                "
              >
                <component
                  :is="lucideIcons['Truck']"
                  class="w-6 h-6 text-red-500"
                  :class="sidebarOpen ? 'size-6 text-white inline-block mr-6' : 'size-6 text-white'"
                />
                <span>Tracking</span>
              </button>
            </li>
          </router-link>
          <router-link
            v-if="can('delivery-boxes')"
            to="/delivery-box"
            class="flex items-center gap-2"
            :active-class="'nav-item-active'"
          >
            <li
              class="parent-menu flex flex-col w-full relative"
              @mouseenter="handleMouseEnter(-1, $event)"
              @mouseleave="handleMouseLeave"
              :class="{
                'sidebar-listitem--hovered': !sidebarOpen && popoverIndex === -1,
              }"
            >
              <button
                class="flex items-center w-full p-4 text-white transition-all duration-200"
                :class="
                  sidebarOpen
                    ? 'justify-start hover:bg-[#292929] text-[1rem]'
                    : 'justify-center hover:bg-[#404040] flex-col gap-2 text-[10px]'
                "
              >
                <component
                  :is="lucideIcons['Boxes']"
                  class="w-6 h-6 text-red-500"
                  :class="
                    sidebarOpen ? 'sizWe-6 text-white inline-block mr-6' : 'size-6 text-white'
                  "
                />
                <span>Delivery Box</span>
              </button>
            </li>
          </router-link>
          <router-link
            v-if="can('generate-barcodes')"
            to="/order-barcode"
            class="flex items-center gap-2"
            :active-class="'nav-item-active'"
          >
            <li
              class="parent-menu flex flex-col w-full relative"
              @mouseenter="handleMouseEnter(-1, $event)"
              @mouseleave="handleMouseLeave"
              :class="{
                'sidebar-listitem--hovered': !sidebarOpen && popoverIndex === -1,
              }"
            >
              <button
                class="flex items-center w-full p-4 text-white transition-all duration-200"
                :class="
                  sidebarOpen
                    ? 'justify-start hover:bg-[#292929] text-[1rem]'
                    : 'justify-center hover:bg-[#404040] flex-col gap-2 text-[10px]'
                "
              >
                <component
                  :is="lucideIcons['Boxes']"
                  class="w-6 h-6 text-red-500"
                  :class="
                    sidebarOpen ? 'sizWe-6 text-white inline-block mr-6' : 'size-6 text-white'
                  "
                />
                <span>Barcode</span>
              </button>
            </li>
          </router-link>
          <router-link
            v-if="canAccessAiAnalytics"
            to="/ai-analytics"
            class="flex items-center gap-2"
            :active-class="'nav-item-active'"
          >
            <li
              class="parent-menu flex flex-col w-full relative"
              @mouseenter="handleMouseEnter(-5, $event)"
              @mouseleave="handleMouseLeave"
              :class="{
                'sidebar-listitem--hovered': !sidebarOpen && popoverIndex === -5,
              }"
            >
              <button
                class="flex items-center w-full p-4 text-white transition-all duration-200"
                :class="
                  sidebarOpen
                    ? 'justify-start hover:bg-[#292929] text-[1rem]'
                    : 'justify-center hover:bg-[#404040] flex-col gap-2 text-[10px]'
                "
              >
                <component
                  :is="lucideIcons['BarChart3']"
                  class="w-6 h-6 text-red-500"
                  :class="
                    sidebarOpen ? 'size-6 text-white inline-block mr-6' : 'size-6 text-white'
                  "
                />
                <span>AI Analytics</span>
              </button>
            </li>
          </router-link>
          <!-- Special: Generate Drawing Sheet -->
          <li
            v-if="permissions.includes('generate-drawing-sheet')"
            class="parent-menu flex flex-col w-full relative"
            @mouseenter="handleMouseEnter(-2, $event)"
            @mouseleave="handleMouseLeave"
            :class="{
              'sidebar-listitem--hovered': !sidebarOpen && popoverIndex === -2,
            }"
          >
            <a
              class="flex items-center w-full p-4 text-white hover:bg-[#292929] transition-all duration-200"
              :class="
                sidebarOpen
                  ? 'justify-start hover:bg-[#292929] text-[1rem]'
                  : 'justify-center hover:bg-[#404040] flex-col gap-2 text-[10px] text-center'
              "
              :href="`${tdsLink}/?auth=${access_token}`"
            >
              <component
                :is="lucideIcons['Sheet']"
                class="w-6 h-6 text-red-500"
                :class="sidebarOpen ? 'size-6 text-white inline-block mr-6' : 'size-6 text-white'"
              />
              <span>Generate Drawing Sheet</span>
            </a>
          </li>
          <li
            v-if="user && permissions.includes('stripe-portal-access')"
            class="parent-menu flex flex-col w-full relative"
            @mouseenter="handleMouseEnter(-2, $event)"
            @mouseleave="handleMouseLeave"
            :class="{
              'sidebar-listitem--hovered': !sidebarOpen && popoverIndex === -2,
            }"
          >
            <a
              class="flex items-center w-full p-4 text-white hover:bg-[#292929] transition-all duration-200 cursor-pointer"
              :class="
                sidebarOpen
                  ? 'justify-start hover:bg-[#292929] text-[1rem]'
                  : 'justify-center hover:bg-[#404040] flex-col gap-2 text-[10px] text-center'
              "
              @click.prevent="handleInvoicePortalClick"
            >
              <component
                :is="lucideIcons['List']"
                class="w-6 h-6 text-red-500"
                :class="sidebarOpen ? 'size-6 text-white inline-block mr-6' : 'size-6 text-white'"
              />
              <span>Invoice Portal</span>
            </a>
          </li>
        </div>

        <!-- Logout Button -->
        <div class="mt-auto">
          <button
            class="text-white text-center mx-auto bg-primary/30 p-4 hover:bg-primary duration-200 rounded-xl flex items-center justify-center relative"
            :class="sidebarOpen ? 'w-[90%]' : 'flex-col gap-2 mb-3'"
            @click="store.logout"
            @mouseenter="handleMouseEnter(-3, $event)"
            @mouseleave="handleMouseLeave"
          >
            <ArrowLeftStartOnRectangleIcon class="size-6 text-white inline-block" />
            <span class="mx-auto" v-if="sidebarOpen">Logout</span>
          </button>
        </div>
      </ul>
    </template>

    <slot />
  </aside>
</template>

<script setup lang="ts">
import MobileLogo from '@/assets/img/avatars/custimoo-logo.png';
import Logo from '@/assets/img/images/web-logo.png';
import Loader from '@/components/Shared/Loader.vue';
import { NavigationMenuItem } from '@/models';
import { useMainStore } from '@/store';
import { ArrowLeftStartOnRectangleIcon, ChevronRightIcon } from '@heroicons/vue/24/solid';
import { onClickOutside } from '@vueuse/core';
import { storeToRefs } from 'pinia';

import { can, logError } from '@/composables';
import { resolveMenuHref } from '@/composables/useMenuHref';
import { http } from '@/interceptors/axiosRequestInterceptor';
import * as lucideIcons from 'lucide-vue-next';
import { computed, defineProps, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
const appURL = import.meta.env.VITE_VUE2_APP_URL;
const tdsLink = import.meta.env.VITE_APP_TDS_LINK;

/** Sidebar entries for screens this app owns stay in-app; the rest go to the legacy admin. */
const menuHref = (path: string | null): string => resolveMenuHref(path, appURL);

defineProps<{ navigation: NavigationMenuItem[] }>();

const store = useMainStore();
const sidebarRef = ref<HTMLElement | null>(null);
const { mobileDevice, sidebarOpen, loadingNavigation, permissions, user, role, globalLoader } =
  storeToRefs(useMainStore());
const access_token = localStorage.getItem('access_token');
const openIndex = ref<number | null>(null);

const popoverIndex = ref<number | null>(null);
const popoverTop = ref<number>(0);

// Tracking Master Dashboard visibility (US #18826, task 18973): strictly gated
// on the view-tracking-dashboard permission. The sidebar entry is hidden unless
// the user's role carries it, and the /tracking route is guarded to match.
const canSeeTracking = computed<boolean>(() => can('view-tracking-dashboard'));

onClickOutside(sidebarRef, () => {
  mobileDevice.value && sidebarOpen.value && (sidebarOpen.value = false);
});

// AB#11459 / AB#14271 — reset menu state on route change so the overlay
// does not linger on the newly loaded page.
const route = useRoute();
watch(
  () => route.fullPath,
  () => {
    if (mobileDevice.value) {
      sidebarOpen.value = false;
    }
  }
);

// AB#11459 / AB#14272 — lock body scroll while the mobile menu is open;
// release it whenever it closes or the component is destroyed.
watch(sidebarOpen, (isOpen) => {
  document.body.style.overflow = isOpen && mobileDevice.value ? 'hidden' : '';
});
onUnmounted(() => {
  document.body.style.overflow = '';
});

const toggleMenu = (index: number): void => {
  openIndex.value = openIndex.value === index ? null : index;
};

const canAccessAiAnalytics = computed(() => {
  const roleName = role.value?.name?.replace(/\s+/g, '').toLowerCase() ?? '';
  if (roleName === 'superadmin') return true;
  if (roleName.includes('merchant')) return true;
  return Boolean(user.value?.company_id);
});

const beforeEnter = (el: Element): void => {
  const element = el as HTMLElement;
  element.style.height = '0px';
};

const enter = (el: Element): void => {
  const element = el as HTMLElement;
  element.style.transition = 'height 0.1s ease-out';
  element.style.height = element.scrollHeight + 'px';
};

const leave = (el: Element): void => {
  const element = el as HTMLElement;
  element.style.transition = 'height 0.1s ease-in';
  element.style.height = '0px';
};

function handleMouseEnter(index: number, event: MouseEvent) {
  if (!sidebarOpen.value) {
    popoverIndex.value = index;
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    popoverTop.value = rect.top;
  }
}

function handleMouseLeave() {
  if (!sidebarOpen.value) {
    popoverIndex.value = null;
  }
}

const handleInvoicePortalClick = async () => {
  try {
    globalLoader.value = true;
    const response = await http.get(`/stripe-billing-portal?redirect_url_v2=true`);
    globalLoader.value = false;
    const portalLink = response.data?.result?.billing_portal_link;
    if (portalLink) {
      window.open(portalLink, '_self');
    } else {
      console.error('No billing portal link returned from API');
    }
  } catch (error) {
    globalLoader.value = false;
    logError('Error fetching the PDF URL:', error);
  }
};
</script>

<style scoped>
/*
 * The sidebar column, and why Logout used to fall off the bottom of it.
 *
 * This was `height: calc(100% - 65px)` on a flex column whose children are a
 * long list of links plus a `mt-auto` Logout. Once the links are taller than
 * that height — which they are on any account that can see more than a dozen
 * menu items — there is no free space for `margin-top: auto` to distribute, so
 * it contributes nothing, and Logout is simply laid out after the overflow.
 *
 * Measured on a 600px sidebar with fifteen links: the list needed 726px, so
 * Logout rendered at 675–726px — 126px BELOW the bottom of the sidebar,
 * reachable only by scrolling it, and clipped by the viewport in between. That
 * is the half-cut button people were reporting; it was never a styling nit, the
 * control was genuinely off-screen.
 *
 * Now: the list is a fixed-height flex column, the LINKS scroll inside it
 * (`min-height: 0` is what actually lets a flex child shrink below its content
 * and scroll), and Logout is a non-shrinking row pinned after them. It no
 * longer depends on there being spare space, so it cannot be pushed anywhere.
 */
.navigation-list {
  margin: 0;
  padding: 0 0 15px;
  list-style: none;
  height: calc(100% - 65px);
  display: flex;
  flex-direction: column;
  width: 100%;
  /* The list owns the scrolling now, so the aside must not double-scroll. */
  overflow: hidden;
}

.navigation-items {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Never shrink, never scroll away. */
.navigation-list > .mt-auto {
  flex: 0 0 auto;
}

.collapse-enter-active,
.collapse-leave-active {
  overflow: hidden;
}

.sidebar-popover {
  position: absolute;
  left: 56px; /* matches collapsed sidebar width */
  min-width: 120px;
  max-width: 170px;
  background: #404040;
  color: #fff;
  border-radius: 0px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
  /* padding: 12px 16px; */
  z-index: 1000;
  border: 1px solid #404040;
  border-radius: 0px 8px 8px 0px;
  /* pointer-events: none;  Remove this if you want popover to be interactive */
}

.sidebar-popover::before {
  content: '';
  position: absolute;
  left: -6px;
  top: 20px;
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 6px solid #3a3a3a;
}

.sidebar-popover::after {
  content: '';
  position: absolute;
  left: -5px;
  top: 20px;
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 6px solid #292929;
}

.sidebar-popover--hovered::before,
.sidebar-popover--hovered::after {
  display: none !important;
}
.sidebar-listitem--hovered {
  border-left: 4px solid theme('colors.primary.DEFAULT') !important;
  /* background: #404040 !important; */
}
.sidebar-label {
  letter-spacing: 0.2em;
}

/* Smooth transitions for sidebar width changes */
.parent-menu button,
.parent-menu a {
  transition: all 0.15s ease-in-out;
}

/* Ensure proper stacking */
.parent-menu {
  position: relative;
  z-index: 1;
  border-left: 4px solid transparent;
}

.parent-menu:hover {
  z-index: 1001;
}

.mt-auto {
  position: relative;
}
.nav-item-active button {
  @apply bg-primary;
}
</style>
